import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { useBranch } from '../context/BranchContext';
import {
  Package, Users, ChefHat, DollarSign, ShoppingCart, BarChart3,
  Briefcase, Database, Shield, Building2, TrendingUp, ClipboardList,
  CreditCard, FileText, Settings, ArrowRight, Search, Calendar,
  Bell, Layers, Activity, Clock
} from 'lucide-react';

const modules = [
  {
    id: 'sales',
    name: 'Sales & POS',
    description: 'Orders, invoicing, point of sale, pricing & promotions',
    icon: CreditCard,
    color: '#2563EB',
    bgColor: '#EFF6FF',
    path: '/sales',
    permission: null,
  },
  {
    id: 'inventory',
    name: 'Inventory & Supply Chain',
    description: 'Stock management, suppliers, purchase orders & transfers',
    icon: Package,
    color: '#059669',
    bgColor: '#ECFDF5',
    path: '/inventory',
    permission: 'inventory_view',
  },
  {
    id: 'production',
    name: 'Production & Kitchen',
    description: 'Recipes, production planning, quality control',
    icon: ChefHat,
    color: '#D97706',
    bgColor: '#FFFBEB',
    path: '/production',
    permission: 'production_log',
  },
  {
    id: 'finance',
    name: 'Finance & Accounting',
    description: 'General ledger, AP/AR, banking, budgets & expenses',
    icon: DollarSign,
    color: '#7C3AED',
    bgColor: '#F5F3FF',
    path: '/finance',
    permission: null,
  },
  {
    id: 'hr',
    name: 'Human Resources',
    description: 'Employees, payroll, attendance, leave & recruitment',
    icon: Briefcase,
    color: '#0891B2',
    bgColor: '#ECFEFF',
    path: '/hr',
    permission: null,
    adminOnly: true,
  },
  {
    id: 'master-data',
    name: 'Master Data',
    description: 'Chart of accounts, tax codes, currencies & assets',
    icon: Database,
    color: '#4F46E5',
    bgColor: '#EEF2FF',
    path: '/master-data',
    permission: null,
    adminOnly: true,
  },
  {
    id: 'reports',
    name: 'Reports & Analytics',
    description: 'Dashboards, comprehensive reports & branch analytics',
    icon: BarChart3,
    color: '#BE185D',
    bgColor: '#FDF2F8',
    path: '/reports',
    permission: 'reports_view',
  },
  {
    id: 'system',
    name: 'System Administration',
    description: 'Users, branches, audit logs, security & configuration',
    icon: Shield,
    color: '#64748B',
    bgColor: '#F8FAFC',
    path: '/system',
    permission: null,
    adminOnly: true,
  },
  {
    id: 'dashboard',
    name: 'Operations Dashboard',
    description: 'Real-time metrics, KPIs and operational overview',
    icon: TrendingUp,
    color: '#EA580C',
    bgColor: '#FFF7ED',
    path: '/dashboard',
    permission: null,
  },
];

function ModuleHome() {
  const navigate = useNavigate();
  const { can, isAdmin } = usePermissions();
  const { currentBranch } = useBranch();
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  const getUserName = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.full_name || parsed.name || null;
      }
    } catch {}
    return null;
  };

  const userName = getUserName();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const visibleModules = modules.filter(m => {
    if (m.adminOnly && !isAdmin) return false;
    if (m.permission && !can(m.permission)) return false;
    return true;
  });

  const filteredModules = visibleModules.filter(m =>
    searchQuery === '' ||
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const today = new Date().toLocaleDateString('en-NG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
          {getGreeting()}{userName ? `, ${userName}` : ''}
        </h1>
        <p className="text-slate-500 mt-1">
          {currentBranch ? `${currentBranch.name} — ` : ''}Welcome back to your workspace
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Layers className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{visibleModules.length}</p>
            <p className="text-xs text-slate-500">Total Modules</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">—</p>
            <p className="text-xs text-slate-500">Active Alerts</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{today}</p>
            <p className="text-xs text-slate-500">Today's Date</p>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search modules..."
            aria-label="Search modules"
            className="w-full pl-10 pr-20 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-slate-100 text-slate-400 text-[10px] rounded border border-slate-200">
            Ctrl+K
          </kbd>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
        {filteredModules.map((mod) => {
          const Icon = mod.icon;
          return (
            <button
              key={mod.id}
              onClick={() => navigate(mod.path)}
              className="group relative bg-white rounded-xl border border-slate-200 p-5 text-left hover:shadow-lg hover:border-slate-300 hover:-translate-y-1 transition-all duration-200 active:scale-[0.98]"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: mod.bgColor }}
              >
                <Icon className="w-6 h-6" style={{ color: mod.color }} />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm lg:text-base">{mod.name}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{mod.description}</p>
              <ArrowRight
                className="absolute top-5 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all duration-200"
              />
            </button>
          );
        })}
        {filteredModules.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No modules match "{searchQuery}"</p>
          </div>
        )}
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'New Order', path: '/sales', icon: ShoppingCart, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'View Inventory', path: '/inventory', icon: Package, color: 'text-emerald-600', bg: 'bg-emerald-50', permission: 'inventory_view' },
            { label: 'Sales Reports', path: '/sales/reports', icon: BarChart3, color: 'text-pink-600', bg: 'bg-pink-50' },
            { label: 'Dashboard', path: '/dashboard', icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Branches', path: '/branches', icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'My Profile', path: '/profile', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Notifications', path: '/notifications', icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Settings', path: '/settings', icon: Settings, color: 'text-slate-600', bg: 'bg-slate-100', adminOnly: true },
          ].filter(item => {
            if (item.adminOnly && !isAdmin) return false;
            if (item.permission && !can(item.permission)) return false;
            return true;
          }).map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className={`w-10 h-10 ${action.bg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${action.color}`} />
                </div>
                <p className="text-xs font-medium text-slate-700">{action.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-slate-400" />
          Recent Activity
        </h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col items-center justify-center py-6 text-slate-400">
            <Clock className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm font-medium">No recent activity</p>
            <p className="text-xs mt-1">Your recent actions will appear here</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModuleHome;
